import { useTranslation } from "react-i18next";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router";
import {
  TEMPERATURE_RECORD_MAX_DAYS,
  type RecordReading,
  type TemperatureRecord,
} from "@/contracts";

import { temperatureRecordQuery } from "@/api/queries";
import { Button } from "@/components/ui/button";
import { Card, CardBody } from "@/components/ui/card";
import { TextField } from "@/components/ui/field";
import { ErrorBlock, LoadingBlock } from "@/components/ui/page";
import { DataTable, EmptyRow, Td, Th } from "@/components/ui/table";
import { useApiClient } from "@/lib/api-context";
import { EM_DASH, formatDevEui } from "@/lib/format";
import { ASSET_TYPE_LABELS } from "./asset-labels";

/**
 * The temperature record for an asset, as a document somebody can print.
 *
 * Printed by the browser rather than rendered to a PDF on the server, which
 * keeps a PDF engine out of the API and the frontend a static bundle. The
 * screen chrome carries `print:hidden` and the layout does the same, so what
 * comes out of the printer is the document and nothing else.
 *
 * It is called a record, not a compliance certificate, and the closing
 * statement says what it is not. See ADR-073.
 */
export function TemperatureRecordPage() {
  const { t } = useTranslation("pages");
  const client = useApiClient();
  const { id = "" } = useParams<{ id: string }>();

  const [period, setPeriod] = useState(defaultPeriod);
  const range = useMemo(() => toInstants(period), [period]);

  const record = useQuery({
    ...temperatureRecordQuery(client, id, range.from, range.to),
    enabled: range.valid,
  });

  return (
    <>
      <div className="print:hidden">
        <div className="mb-5">
          <Link to="/assets" className="text-sm text-ink-muted underline underline-offset-2">
            ← Assets
          </Link>
          <h1 className="mt-2 text-lg font-semibold tracking-tight text-ink">{t("tempRecordTitle")}</h1>
          <p className="mt-0.5 text-sm text-ink-muted">
            What was measured over a period, how much of it was actually covered, and every run
            outside the target range. At most {TEMPERATURE_RECORD_MAX_DAYS} days in one record.
          </p>
        </div>

        <Card className="mb-4">
          <CardBody>
            <div className="grid items-end gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <TextField
                label={t("tempRecordFrom")}
                type="date"
                value={period.from}
                max={period.to}
                onChange={(event) => setPeriod({ ...period, from: event.target.value })}
              />
              <TextField
                label={t("tempRecordTo")}
                type="date"
                value={period.to}
                min={period.from}
                onChange={(event) => setPeriod({ ...period, to: event.target.value })}
              />

              <div className="flex gap-2 sm:col-span-2">
                <Button onClick={() => window.print()} disabled={!record.data}>
                  Print
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => record.data && downloadCsv(record.data)}
                  disabled={!record.data}
                >
                  Download CSV
                </Button>
              </div>
            </div>

            {range.valid ? null : (
              <p className="mt-3 text-sm text-danger">
                The period must start before it ends and cover at most {TEMPERATURE_RECORD_MAX_DAYS}{" "}
                days.
              </p>
            )}
          </CardBody>
        </Card>
      </div>

      {record.isPending && range.valid ? <LoadingBlock label={t("tempRecordBuilding")} /> : null}
      {record.isError ? (
        <ErrorBlock error={record.error} onRetry={() => void record.refetch()} />
      ) : null}
      {record.data ? <RecordDocument record={record.data} /> : null}
    </>
  );
}

/**
 * The document itself.
 *
 * Every time here is printed in the organization's timezone, which the header
 * names. The rest of the interface uses the browser's zone and says so in
 * `format.ts`; a document that may be filed cannot leave the question of
 * which clock it is on to whoever opens it.
 */
function RecordDocument({ record }: { record: TemperatureRecord }) {
  const { t } = useTranslation("pages");
  const at = useMemo(() => timeFormatter(record.timeZone), [record.timeZone]);

  return (
    <article className="space-y-6 text-sm text-ink">
      <header className="border-b border-border-subtle pb-4">
        <h2 className="text-base font-semibold">{t("tempRecordTitle")}</h2>
        <dl className="mt-3 grid gap-x-6 gap-y-1 sm:grid-cols-2">
          <Entry term="Organization" value={record.organizationName} />
          <Entry term="Site" value={record.siteName} />
          <Entry term="Asset" value={record.assetName} />
          <Entry term="Asset type" value={ASSET_TYPE_LABELS[record.assetType]} />
          <Entry term="Period" value={`${at(record.range.from)} to ${at(record.range.to)}`} />
          <Entry term="Times shown in" value={record.timeZone} />
          <Entry
            term="Target range"
            value={
              record.judged
                ? `${bound(record.targetTempMinC)} to ${bound(record.targetTempMaxC)}`
                : "None configured"
            }
          />
          <Entry
            term="Produced"
            value={`${at(record.generatedAt)}${
              record.generatedByEmail ? ` by ${record.generatedByEmail}` : ""
            }`}
          />
        </dl>
      </header>

      {record.judged ? null : (
        <Statement>
          This asset has no target temperature range, so the readings below were never compared
          against anything. The record is a log of what was measured and not an assessment of it: an
          empty excursion list here means nothing was judged, which is not the same as nothing
          having gone wrong.
        </Statement>
      )}

      <Section title={t("tempRecordCoverage")}>
        <p className="mb-3 text-ink-muted">
          {record.coverage.expectedReadings === null
            ? "No temperature sensor is attached to this asset, so no readings were expected."
            : `${record.coverage.receivedReadings} readings arrived of ${record.coverage.expectedReadings} expected` +
              (record.coverage.percent === null
                ? ""
                : ` — ${record.coverage.percent}% of the period`) +
              `. A stretch with no readings is listed below once it passes ${record.coverage.gapThresholdMinutes} minutes, which is the point at which this platform calls a device offline; shorter misses are counted in the percentage above rather than listed.`}
        </p>

        <DataTable caption="Gaps in the record">
          <thead>
            <tr>
              <Th>{t("tempColGapStarted")}</Th>
              <Th>{t("tempColGapEnded")}</Th>
              <Th>{t("tempColDuration")}</Th>
            </tr>
          </thead>

          <tbody>
            {record.coverage.gaps.length === 0 ? (
              <EmptyRow colSpan={3}>{t("tempEmptyGaps")}</EmptyRow>
            ) : (
              record.coverage.gaps.map((gap) => (
                <tr key={gap.startedAt}>
                  <Td>{at(gap.startedAt)}</Td>
                  <Td>{at(gap.endedAt)}</Td>
                  <Td>{duration(gap.minutes)}</Td>
                </tr>
              ))
            )}
          </tbody>
        </DataTable>
      </Section>

      <Section title={t("tempRecordMeasured")}>
        <dl className="grid gap-x-6 gap-y-1 sm:grid-cols-2">
          {record.temperature ? (
            <>
              <Entry term="Lowest temperature" value={`${record.temperature.min} °C`} />
              <Entry term="Highest temperature" value={`${record.temperature.max} °C`} />
              <Entry term="Mean temperature" value={`${record.temperature.mean} °C`} />
              <Entry
                term="Readings with a temperature"
                value={String(record.temperature.readings)}
              />
            </>
          ) : (
            <Entry term="Temperature" value="No reading in the period carried one" />
          )}

          {record.humidity ? (
            <>
              <Entry term="Lowest humidity" value={`${record.humidity.min} %`} />
              <Entry term="Highest humidity" value={`${record.humidity.max} %`} />
            </>
          ) : null}
        </dl>
      </Section>

      {record.judged ? (
        <Section title={t("tempRecordRunsOutside")}>
          <p className="mb-3 text-ink-muted">
            {record.excursions.length === 0
              ? "No reading fell outside the target range."
              : `${record.excursions.length} run${record.excursions.length === 1 ? "" : "s"}, ${duration(record.minutesOutOfRange)} in total. Each run is measured from the first reading outside the range to the last one, so it understates by up to one reporting interval at each end: the temperature crossed the bound somewhere between the last good reading and the first bad one, and nothing observed when.`}
          </p>

          <DataTable caption="Runs outside the target range">
            <thead>
              <tr>
                <Th>{t("tempColStarted")}</Th>
                <Th>{t("tempColEnded")}</Th>
                <Th>{t("tempColDuration")}</Th>
                <Th>{t("tempColReadings")}</Th>
                <Th>{t("tempColFurthest")}</Th>
                <Th>{t("tempColBound")}</Th>
              </tr>
            </thead>

            <tbody>
              {record.excursions.length === 0 ? (
                <EmptyRow colSpan={6}>{t("tempEmptyOutside")}</EmptyRow>
              ) : (
                record.excursions.map((run) => (
                  <tr key={run.startedAt}>
                    <Td>{at(run.startedAt)}</Td>
                    <Td>{at(run.endedAt)}</Td>
                    <Td>{duration(run.minutes)}</Td>
                    <Td>{run.readings}</Td>
                    <Td>{run.extremeC} °C</Td>
                    <Td>{run.bound === "MAX" ? "Too warm" : "Too cold"}</Td>
                  </tr>
                ))
              )}
            </tbody>
          </DataTable>
        </Section>
      ) : null}

      <Section title={t("tempRecordIncidents")}>
        <p className="mb-3 text-ink-muted">
          What the alert engine opened during the period. It is not the same list as the runs above:
          an alert rule needs the condition to hold for its full duration, so a short run raises no
          incident, and an offline device raises one without any reading at all.
        </p>

        <DataTable caption="Incidents raised">
          <thead>
            <tr>
              <Th>{t("tempColStarted")}</Th>
              <Th>{t("colType")}</Th>
              <Th>{t("colSeverity")}</Th>
              <Th>{t("colStatus")}</Th>
              <Th>{t("tempColResolved")}</Th>
            </tr>
          </thead>

          <tbody>
            {record.incidents.length === 0 ? (
              <EmptyRow colSpan={5}>{t("tempEmptyIncidents")}</EmptyRow>
            ) : (
              record.incidents.map((incident) => (
                <tr key={incident.id}>
                  <Td>{at(incident.startedAt)}</Td>
                  <Td>{incident.type.replaceAll("_", " ").toLowerCase()}</Td>
                  <Td>{incident.severity.toLowerCase()}</Td>
                  <Td>{incident.status.toLowerCase()}</Td>
                  <Td>{incident.resolvedAt ? at(incident.resolvedAt) : EM_DASH}</Td>
                </tr>
              ))
            )}
          </tbody>
        </DataTable>
      </Section>

      <Section title={t("tempRecordInstruments")}>
        <DataTable caption="Instruments">
          <thead>
            <tr>
              <Th>{t("tempColDevEui")}</Th>
              <Th>{t("tempColInstrument")}</Th>
              <Th>{t("tempColReportingInterval")}</Th>
              <Th>{t("tempColWatchedFrom")}</Th>
              <Th>{t("tempColWatchedTo")}</Th>
            </tr>
          </thead>

          <tbody>
            {record.devices.length === 0 ? (
              <EmptyRow colSpan={5}>{t("tempEmptyInstruments")}</EmptyRow>
            ) : (
              record.devices.map((device) => (
                <tr key={device.id}>
                  <Td>{formatDevEui(device.devEui)}</Td>
                  <Td>
                    {device.manufacturer} {device.model}
                  </Td>
                  <Td>
                    {Math.round(device.reportingIntervalSeconds / 60)} min
                    {device.intervalAssumed ? " (assumed)" : ""}
                  </Td>
                  <Td>{at(device.watchingFrom)}</Td>
                  <Td>{at(device.watchingTo)}</Td>
                </tr>
              ))
            )}
          </tbody>
        </DataTable>
      </Section>

      <Statement>
        <strong className="font-semibold">{t("tempDisclaimerTitle")}</strong> It is
        the set of readings these instruments reported over the period, with the gaps and the runs
        outside the target range stated rather than omitted. It is not a calibrated measurement: no
        calibration certificate is held for any of the instruments named above, none is recorded in
        this system, and the readings are therefore not traceable to a measurement standard. This
        record does not by itself establish compliance with any regulation; what it evidences is
        that the asset was monitored and what the monitoring saw. The registration dates in the
        instrument list are when each sensor was registered in this system, which is not necessarily
        when it was installed.
      </Statement>
    </article>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="break-inside-avoid">
      <h3 className="mb-2 text-sm font-semibold text-ink">{title}</h3>
      {children}
    </section>
  );
}

function Entry({ term, value }: { term: string; value: string }) {
  return (
    <div className="flex gap-2">
      <dt className="text-ink-muted">{term}:</dt>
      <dd className="font-medium">{value}</dd>
    </div>
  );
}

/** The plain-language caveats, which must survive printing. */
function Statement({ children }: { children: React.ReactNode }) {
  return (
    <p className="break-inside-avoid rounded-lg border border-border-subtle bg-surface-muted p-3 text-xs leading-relaxed text-ink-muted">
      {children}
    </p>
  );
}

function bound(value: number | null): string {
  return value === null ? "no limit" : `${value} °C`;
}

function duration(minutes: number): string {
  if (minutes < 60) {
    return `${minutes} min`;
  }

  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;

  return rest === 0 ? `${hours} h` : `${hours} h ${rest} min`;
}

/**
 * A formatter bound to the organization's timezone, in `en-GB` like the rest
 * of the interface, and with `hourCycle: "h23"` so midnight reads as 00:00
 * rather than as 24:00 on the ICU versions that do that.
 */
function timeFormatter(timeZone: string): (iso: string) => string {
  const formatter = new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
    timeZone,
  });

  return (iso) => formatter.format(new Date(iso));
}

/** The last 30 days, which fits inside the maximum period. */
function defaultPeriod(): { from: string; to: string } {
  const today = new Date();
  const start = new Date(today.getTime() - 29 * 24 * 60 * 60 * 1_000);

  return { from: isoDate(start), to: isoDate(today) };
}

function isoDate(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function pad(value: number): string {
  return String(value).padStart(2, "0");
}

/**
 * The two dates as instants: the whole of the first day to the end of the
 * last, read in the browser's own zone.
 *
 * The document prints the resulting window in the organization's timezone, so
 * an operator working from somewhere else sees the shift instead of the
 * period silently meaning something other than the dates they typed.
 */
function toInstants(period: { from: string; to: string }): {
  from: string;
  to: string;
  valid: boolean;
} {
  const from = new Date(`${period.from}T00:00:00`);
  const to = new Date(`${period.to}T23:59:59`);
  const spanDays = (to.getTime() - from.getTime()) / (24 * 60 * 60 * 1_000);

  return {
    from: Number.isNaN(from.getTime()) ? "" : from.toISOString(),
    to: Number.isNaN(to.getTime()) ? "" : to.toISOString(),
    valid: !Number.isNaN(spanDays) && spanDays > 0 && spanDays <= TEMPERATURE_RECORD_MAX_DAYS,
  };
}

const CSV_COLUMNS = [
  "measured_at_utc",
  "measured_at_local",
  "time_zone",
  "device_deveui",
  "temperature_c",
  "humidity_percent",
  "within_target_range",
] as const;

/**
 * The readings as a file, built here from the response rather than fetched
 * from a second endpoint.
 *
 * The access token lives in memory only, so a plain download link cannot
 * authenticate and any file has to come through the API client anyway. At
 * that point a second server route buys nothing, and the file is guaranteed
 * to hold exactly the readings the document was built from (ADR-073).
 */
function downloadCsv(record: TemperatureRecord): void {
  const at = timeFormatter(record.timeZone);
  // By DevEUI rather than by our internal id: the row has to mean something
  // to whoever opens the file, and the serial number is the instrument.
  const devEuis = new Map(record.devices.map((device) => [device.id, device.devEui]));
  const rows = record.readings.map((reading) =>
    csvRow(reading, record.timeZone, at, devEuis.get(reading.deviceId) ?? reading.deviceId),
  );
  const csv = [CSV_COLUMNS.join(","), ...rows].join("\r\n");

  const blob = new Blob([`\uFEFF${csv}`], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.download = fileName(record);
  link.click();
  URL.revokeObjectURL(url);
}

function csvRow(
  reading: RecordReading,
  timeZone: string,
  at: (iso: string) => string,
  devEui: string,
): string {
  return [
    reading.recordedAt,
    at(reading.recordedAt),
    timeZone,
    devEui,
    reading.temperatureC ?? "",
    reading.humidityPercent ?? "",
    reading.withinRange === null ? "" : String(reading.withinRange),
  ]
    .map(escapeCsv)
    .join(",");
}

function escapeCsv(value: string | number): string {
  const text = String(value);

  return /[",\r\n]/.test(text) ? `"${text.replaceAll('"', '""')}"` : text;
}

function fileName(record: TemperatureRecord): string {
  const slug = record.assetName
    .toLowerCase()
    .replaceAll(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");

  return `temperature-record-${slug || record.assetId}-${record.range.from.slice(0, 10)}-to-${record.range.to.slice(0, 10)}.csv`;
}
